import { z } from 'zod';
import { cursorQuerySchema, pageSchema, timestampSchema } from './admin.js';
import { userTypeSchema } from './auth.js';
import { attachmentSchema } from './attachments.js';
import { visibilitySchema, type ProjectPermission, type Visibility } from './permissions.js';
import { labelSchema, personSchema } from './projects.js';
import { commentBodyInputSchema, commentRichTextSchema } from './rich-text.js';
import { taskStatusRefSchema } from './tasks.js';

/** COL-01, COL-02, COL-04: comments with mentions, activity timeline of a task. */

/** Problem `type` URIs of comments, so the UI can show specific messages. */
export const commentProblemTypes = {
  /** A mentioned person is no member or could not see the task or comment. */
  invalidMention: 'urn:pm-tool:problem:comment-invalid-mention',
} as const;

/**
 * Comments the viewer sees on a visible task (docs/04, section 5): external ones always, internal
 * ones with `comment.internal` (never held by externals, PERM-04).
 */
export function canSeeComment(
  permissions: ReadonlySet<ProjectPermission>,
  visibility: Visibility,
): boolean {
  return visibility === 'external' || permissions.has('comment.internal');
}

/** Writing: internal comments need `comment.internal`, external ones `comment.create`. */
export function canWriteComment(
  permissions: ReadonlySet<ProjectPermission>,
  visibility: Visibility,
): boolean {
  return permissions.has(visibility === 'internal' ? 'comment.internal' : 'comment.create');
}

/** COL-02: internal users write internal comments by default (if they may). */
export function defaultCommentVisibility(
  permissions: ReadonlySet<ProjectPermission>,
): Visibility | null {
  if (permissions.has('comment.internal')) return 'internal';
  return permissions.has('comment.create') ? 'external' : null;
}

export const commentSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  author: personSchema.nullable(),
  body: commentRichTextSchema,
  visibility: visibilitySchema,
  createdAt: timestampSchema,
  editedAt: timestampSchema.nullable(),
  /** Own comment that the viewer may still change (not in archived projects). */
  canEdit: z.boolean(),
});
export type Comment = z.infer<typeof commentSchema>;

export const commentIdParamsSchema = z.object({ commentId: z.uuid() });

export const createCommentBodySchema = z.object({
  body: commentBodyInputSchema,
  /** Default: internal for internal users with comment.internal (COL-02); externals: external. */
  visibility: visibilitySchema.optional(),
});
export type CreateCommentBody = z.infer<typeof createCommentBodySchema>;

export const updateCommentBodySchema = z
  .object({ body: commentBodyInputSchema, visibility: visibilitySchema })
  .partial()
  .refine((b) => b.body !== undefined || b.visibility !== undefined, 'Nothing to change');
export type UpdateCommentBody = z.infer<typeof updateCommentBodySchema>;

// --- Mention candidates ------------------------------------------------------------------------

export const mentionCandidatesQuerySchema = z.object({
  /** Visibility of the comment being written: internal comments never mention externals. */
  visibility: visibilitySchema,
  q: z.string().trim().max(100).optional(),
});
export type MentionCandidatesQuery = z.infer<typeof mentionCandidatesQuerySchema>;

export const mentionCandidateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  userType: userTypeSchema,
  /** Profile picture (ADM-07). */
  avatarUrl: z.string().nullable(),
});
export type MentionCandidate = z.infer<typeof mentionCandidateSchema>;

export const MENTION_CANDIDATE_LIMIT = 20;
export const mentionCandidatesResponseSchema = z.object({
  items: z.array(mentionCandidateSchema),
});
export type MentionCandidatesResponse = z.infer<typeof mentionCandidatesResponseSchema>;

// --- Timeline (COL-04) -------------------------------------------------------------------------

/**
 * Activity fields external viewers never see (docs/04, section 5): internal settings and, for
 * moved tasks, the source project and old key. Entries left without changes are dropped.
 */
export const internalActivityFields = ['visibility', 'estimateHours', 'project', 'key'] as const;

export const timelineFilterSchema = z.enum(['all', 'comments', 'changes']);
export type TimelineFilter = z.infer<typeof timelineFilterSchema>;

export const timelineQuerySchema = cursorQuerySchema.extend({
  filter: timelineFilterSchema.default('all'),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type TimelineQuery = z.infer<typeof timelineQuerySchema>;

const plainValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

/**
 * One changed field. References are resolved for display: statuses and labels of the project
 * (`null`/left out when they no longer exist), people as `Person` (hidden per E-08).
 */
export const timelineChangeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('status'),
    field: z.literal('status'),
    from: taskStatusRefSchema.nullable(),
    to: taskStatusRefSchema.nullable(),
  }),
  z.object({
    kind: z.literal('people'),
    field: z.string(),
    from: z.array(personSchema),
    to: z.array(personSchema),
  }),
  z.object({
    kind: z.literal('labels'),
    field: z.string(),
    from: z.array(labelSchema),
    to: z.array(labelSchema),
  }),
  z.object({
    kind: z.literal('value'),
    field: z.string(),
    from: plainValueSchema,
    to: plainValueSchema,
  }),
]);
export type TimelineChange = z.infer<typeof timelineChangeSchema>;

export const timelineActivitySchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor: personSchema.nullable(),
  createdAt: timestampSchema,
  changes: z.array(timelineChangeSchema),
  /** AI-05: made through a confirmed action of the AI assistant. */
  viaAssistant: z.boolean(),
});
export type TimelineActivity = z.infer<typeof timelineActivitySchema>;

/** Newest first; comments, attachments (phase 5c) and changes of the task mixed. */
export const timelineItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('comment'), comment: commentSchema }),
  z.object({ kind: z.literal('attachment'), attachment: attachmentSchema }),
  z.object({ kind: z.literal('activity'), activity: timelineActivitySchema }),
]);
export type TimelineItem = z.infer<typeof timelineItemSchema>;

export const timelineResponseSchema = pageSchema(timelineItemSchema);
export type TimelineResponse = z.infer<typeof timelineResponseSchema>;
